import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import {
  getElementTourRect,
  getTourCardSx,
  getTourCardInnerSx,
} from './tourPosition';

const ACCENT = '#137274';
const PINK = '#f50057';

export const LIVE_ROOM_TOUR_KEY = 'ch_live_coding_room_tour_v1';
export const LIVE_TOUR_START_EVENT = 'ch-start-live-tour';
export const LIVE_TOUR_COMPLETE_EVENT = 'ch-live-tour-complete';

/** @deprecated landing tour removed — kept so old localStorage keys don’t break anything */
export const LIVE_LANDING_TOUR_KEY = 'ch_live_coding_landing_tour_v1';

const roomSteps = [
  {
    id: 'welcome-room',
    placement: 'center',
    eyebrow: 'You’re in',
    title: 'Welcome to your Live Coding room',
    body: 'This quick tour highlights the controls you’ll use most — Run, Invite, editor settings, and video.',
    cta: 'Let’s go',
  },
  {
    id: 'header',
    target: '[data-tour="live-header-actions"]',
    placement: 'bottom',
    eyebrow: 'Session controls',
    title: 'Run code, invite, or share screen',
    body: 'Run executes for everyone in the room. Invite copies a join link. Share screen when you need to show another window.',
  },
  {
    id: 'editor',
    target: '[data-tour="live-editor-toolbar"]',
    placement: 'bottom',
    eyebrow: 'Shared editor',
    title: 'Language & editor settings',
    body: 'Pick from 11 languages — Python, Java, Go, Rust, TypeScript, and more. Open Settings for theme and font size — saved on this device. Your peer sees your cursor as you type.',
  },
  {
    id: 'whiteboard',
    target: '[data-tour="live-main-pane-tabs"]',
    placement: 'right',
    eyebrow: 'Draw it out',
    title: 'Editor or Whiteboard',
    body: 'Switch to the Whiteboard tab for a shared canvas — draw, highlight, or drop in text, synced live with your peer just like the code editor.',
  },
  {
    id: 'video',
    target: '[data-tour="live-video-pane"]',
    placement: 'left',
    eyebrow: 'Face-to-face',
    title: 'Video & mic controls',
    body: 'Mute, camera, and background blur live on your tile. Your peer appears below when they join.',
  },
  {
    id: 'finish-room',
    placement: 'center',
    eyebrow: 'You’re set',
    title: 'Start coding together',
    body: 'Replay this tour anytime with the Tour button in the header. Happy pairing!',
    cta: 'Start coding',
  },
];

export const isLiveRoomTourDone = () => {
  try {
    return localStorage.getItem(LIVE_ROOM_TOUR_KEY) === '1';
  } catch {
    return true;
  }
};

const markTourDone = () => {
  try {
    localStorage.setItem(LIVE_ROOM_TOUR_KEY, '1');
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(LIVE_TOUR_COMPLETE_EVENT));
};

const LiveCodingTour = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { roomId } = useParams();

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [forced, setForced] = useState(false);

  const inRoom = Boolean(roomId);
  const steps = roomSteps;

  const step = steps[stepIndex] || steps[0];
  const progress = ((stepIndex + 1) / steps.length) * 100;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const closeTour = useCallback((completed = true) => {
    setOpen(false);
    setForced(false);
    setStepIndex(0);
    setRect(null);
    if (completed) markTourDone();
  }, []);

  const measure = useCallback(() => {
    const current = steps[stepIndex];
    if (!current?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(current.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({
      behavior: 'smooth',
      block: isMobile ? 'center' : 'nearest',
      inline: 'nearest',
    });
    setRect(getElementTourRect(current.target));
  }, [stepIndex, steps, isMobile]);

  const startTour = useCallback(
    (force = false) => {
      if (!inRoom) return;
      if (!force && isLiveRoomTourDone()) return;

      const ready = () =>
        document.querySelector('[data-tour="live-editor-toolbar"]') &&
        document.body.dataset.liveNameReady === '1';

      if (!ready()) {
        let tries = 0;
        const id = setInterval(() => {
          tries += 1;
          if (ready() || tries > 40) {
            clearInterval(id);
            if (ready() || force) {
              setForced(force);
              setStepIndex(0);
              setOpen(true);
            }
          }
        }, 250);
        return;
      }

      setForced(force);
      setStepIndex(0);
      setOpen(true);
    },
    [inRoom],
  );

  // Auto-start only on first visit inside a room
  useEffect(() => {
    if (!inRoom || isLiveRoomTourDone()) return undefined;
    const timer = setTimeout(() => startTour(false), 1200);
    return () => clearTimeout(timer);
  }, [inRoom, roomId, startTour]);

  // Manual replay (room header Tour button)
  useEffect(() => {
    const onStart = () => startTour(true);
    window.addEventListener(LIVE_TOUR_START_EVENT, onStart);
    return () => window.removeEventListener(LIVE_TOUR_START_EVENT, onStart);
  }, [startTour]);

  // Skip missing targets
  useEffect(() => {
    if (!open) return undefined;
    const current = steps[stepIndex];
    if (!current?.target) return undefined;
    const timer = setTimeout(() => {
      if (!document.querySelector(current.target) && stepIndex < steps.length - 1) {
        setStepIndex((i) => i + 1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [open, stepIndex, steps]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(measure, 80);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const id = setInterval(measure, 500);
    return () => {
      clearTimeout(t);
      clearInterval(id);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, measure, stepIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTour(true);
      if (e.key === 'ArrowRight') {
        if (stepIndex >= steps.length - 1) closeTour(true);
        else setStepIndex((i) => i + 1);
      }
      if (e.key === 'ArrowLeft') setStepIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stepIndex, steps.length, closeTour]);

  const goNext = () => {
    if (isLast) {
      closeTour(true);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  if (!open || !step || !inRoom) return null;

  const isCentered = !rect || step.placement === 'center';
  const cardSx = getTourCardSx({
    rect,
    placement: step.placement,
    isMobile,
    zIndex: 1502,
  });
  const innerSx = getTourCardInnerSx(isMobile, isCentered);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500 }}>
      <Box
        onClick={() => closeTour(true)}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: isCentered ? alpha('#020808', 0.64) : 'transparent',
        }}
      />

      {rect && !isCentered && (
        <Box
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 2.5,
            boxShadow: `0 0 0 9999px ${alpha('#020808', 0.64)}`,
            outline: `2px solid ${alpha('#7ed4d4', 0.95)}`,
            zIndex: 1501,
            pointerEvents: 'none',
            transition:
              'top 0.22s ease, left 0.22s ease, width 0.22s ease, height 0.22s ease',
          }}
        />
      )}

      <Box sx={cardSx} onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <Box
            key={step.id}
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            sx={innerSx}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${isDark ? alpha('#7ed4d4', 0.25) : alpha(ACCENT, 0.2)}`,
                bgcolor: isDark ? '#121a1c' : '#ffffff',
                boxShadow: isDark
                  ? '0 24px 64px rgba(0,0,0,0.55)'
                  : '0 24px 64px rgba(15,60,70,0.18)',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 3,
                  bgcolor: alpha(ACCENT, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: ACCENT },
                }}
              />
              <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1,
                      py: 0.4,
                      borderRadius: 99,
                      bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                      color: isDark ? '#7ed4d4' : ACCENT,
                      maxWidth: '85%',
                    }}
                  >
                    {isLast ? (
                      <VideocamOutlinedIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.eyebrow}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => closeTour(true)}
                    aria-label="Skip tour"
                    sx={{ flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    letterSpacing: '-0.03em',
                    mb: 0.75,
                    lineHeight: 1.25,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    color: isDark ? '#8b949e' : '#5f6b73',
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    lineHeight: 1.55,
                    mb: 2,
                  }}
                >
                  {step.body}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? '#6e7681' : '#8a969c', fontWeight: 700 }}
                  >
                    {stepIndex + 1} / {steps.length}
                    {forced ? ' · replay' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    {!isFirst && (
                      <Button
                        size="small"
                        onClick={goBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: isDark ? '#c9d1d9' : '#334',
                        }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={goNext}
                      endIcon={!isLast ? <ArrowForwardIcon /> : undefined}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: isLast ? PINK : ACCENT,
                        px: 1.75,
                        '&:hover': { bgcolor: isLast ? '#c40048' : '#0f5c5e' },
                      }}
                    >
                      {step.cta || (isLast ? 'Finish' : 'Next')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default LiveCodingTour;
